import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { authservice } from '../../../../components/api/authservice';
import { isExternalUser, type UserDetailDto } from '../../interfaces/Users';
import { useAccessTheme } from './useAccessTheme';

interface ViewUserModalProps {
  userId: string;
  onClose: () => void;
}

type ViewTab = 'overview' | 'access' | 'directory';

function display(value: string | null | undefined): string {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed : '-';
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function portalAccessLabel(status?: string): string {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'Active';
    case 'invited':
      return 'Invited';
    case 'suspended':
      return 'Suspended';
    default:
      return 'None';
  }
}

function directoryStatusLabel(status?: string | null): string {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'Directory Active';
    case 'disabled':
      return 'Directory Disabled';
    case 'deleted':
      return 'Deleted';
    default:
      return 'Unknown';
  }
}

function loginTypeLabel(loginType?: string): string {
  switch ((loginType || '').toLowerCase()) {
    case 'azuread':
      return 'Microsoft Entra';
    case 'okta':
      return 'Okta';
    case 'google':
      return 'Google';
    case 'saml':
      return 'SAML';
    case 'custom':
      return 'Custom';
    case 'external':
      return 'External';
    default:
      return 'Local';
  }
}

function providerLabel(provider?: string | null): string {
  if (!provider) return 'Microsoft Entra';
  if (provider.toLowerCase() === 'microsoftentra' || provider.toLowerCase() === 'azuread') {
    return 'Microsoft Entra';
  }
  return provider;
}

const ViewUserModal: React.FC<ViewUserModalProps> = ({ userId, onClose }) => {
  const t = useAccessTheme();
  const [tab, setTab] = useState<ViewTab>('overview');
  const [user, setUser] = useState<UserDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    authservice.getUserDetail(userId)
      .then((res) => {
        if (!cancelled) setUser(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load user.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId]);

  const directoryManaged = !!(user && isExternalUser(user));
  const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : '';

  const field = (label: string, value: React.ReactNode) => (
    <div>
      <div className={`text-xs font-medium mb-1 ${t.muted}`}>{label}</div>
      <div className={`text-sm ${t.title}`}>{value}</div>
    </div>
  );

  const tabBtn = (id: ViewTab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === id ? t.btnPrimary : t.btnGhost}`}
    >
      {label}
    </button>
  );

  return createPortal(
    <div className={`fixed inset-0 z-[150] flex items-center justify-center p-4 ${t.overlay}`}>
      <div className={`rounded-xl shadow-xl w-full max-w-2xl overflow-hidden border ${t.panel}`}>
        <div className={`flex items-start justify-between px-5 py-4 border-b ${t.header}`}>
          <div className="min-w-0">
            <h3 className={`text-base font-semibold ${t.title}`}>View User</h3>
            {user ? (
              <>
                <p className={`text-sm mt-1 ${t.title}`}>{fullName}</p>
                <p className={`text-xs mt-0.5 ${t.muted}`}>{user.email}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide border ${user.isActive ? t.badgeAccent : t.badge}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide border ${t.badge}`}>
                    Portal Access {portalAccessLabel(user.portalAccessStatus)}
                  </span>
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide border ${t.badge}`}>
                    {directoryManaged ? providerLabel(user.externalProvider) : 'Local'}
                  </span>
                  {directoryManaged ? (
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide border ${t.badge}`}>
                      {directoryStatusLabel(user.directoryStatus)}
                    </span>
                  ) : null}
                </div>
              </>
            ) : (
              <p className={`text-xs mt-1 ${t.muted}`}>Read-only user profile</p>
            )}
          </div>
          <button type="button" onClick={onClose} className={`p-1.5 rounded-lg ${t.closeBtn}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-4 flex flex-wrap gap-2">
          {tabBtn('overview', 'Overview')}
          {tabBtn('access', 'Access')}
          {tabBtn('directory', 'Directory')}
        </div>

        <div className="p-5 min-h-[280px]">
          {loading ? (
            <p className={`text-sm ${t.muted}`}>Loading user...</p>
          ) : error ? (
            <div className={`border rounded-lg px-3 py-2 text-xs ${t.errorBox}`}>{error}</div>
          ) : user && tab === 'overview' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field('First Name', display(user.firstName))}
              {field('Last Name', display(user.lastName))}
              {field('Email', display(user.email))}
              {field('Phone Number', display(user.phoneNumber))}
              {field('Primary Role', display(user.roleName))}
              {field('Department', display(user.department?.name))}
              {field('User Status', user.isActive ? 'Active' : 'Inactive')}
              {field('IsActive', user.isActive ? 'Yes' : 'No')}
              {field('Portal Access Status', portalAccessLabel(user.portalAccessStatus))}
              {field('Last Login', formatDate(user.lastLoginAt))}
              {field('Created At', formatDate(user.createdAt))}
            </div>
          ) : user && tab === 'access' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field('Primary Role', display(user.roleName))}
              {field('Portal Access', portalAccessLabel(user.portalAccessStatus))}
              {field('User Active', user.isActive ? 'Yes' : 'No')}
              {field('Email Confirmed', user.emailConfirmed ? 'Yes' : 'No')}
              {field('Login Type', loginTypeLabel(user.loginType))}
            </div>
          ) : user && tab === 'directory' && directoryManaged ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('Source', providerLabel(user.externalProvider))}
                {field('Imported', user.isImported ? 'Yes' : 'No')}
                {field('External Provider', display(user.externalProvider))}
                {field('Directory Status', directoryStatusLabel(user.directoryStatus).replace('Directory ', ''))}
                {field('Directory Connection', display(user.directoryConnection?.name))}
                {field('Last Synced', formatDate(user.lastSyncedAt))}
                {field('Sync Enabled', user.syncEnabled ? 'Yes' : 'No')}
                {field('Job Title', display(user.jobTitle))}
                {field('Office Location', display(user.officeLocation))}
                {field('Employee ID', display(user.employeeId))}
                {field('Manager', display(user.manager?.name || user.manager?.email))}
                {field('Department', display(user.department?.name))}
              </div>
              {user.externalProviderUserId ? (
                <div className={`rounded-lg border px-3 py-2.5 ${t.border}`}>
                  <div className={`text-xs font-medium mb-1 ${t.muted}`}>Advanced</div>
                  {field('External Provider User ID', user.externalProviderUserId)}
                </div>
              ) : null}
            </div>
          ) : user ? (
            <div>
              <div className={`text-xs font-medium mb-1 ${t.muted}`}>Directory</div>
              <div className={`text-sm ${t.title}`}>Not directory managed</div>
            </div>
          ) : null}
        </div>

        <div className={`px-5 py-4 border-t flex justify-end ${t.footer}`}>
          <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${t.btnSecondary}`}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewUserModal;
